import {
  configureStore,
  type Middleware,
  type Reducer,
  type ReducersMapObject,
} from '@reduxjs/toolkit';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

type CacheKeyValue =
  | string
  | number
  | boolean
  | null
  | CacheKeyValue[]
  | { [key: string]: CacheKeyValue };

type BaseApiError = {
  message: string;
};

export type TriedgeFeatureBundleOptions<
  TTransport = unknown,
  TSeedResolver = unknown,
> = {
  transport: TTransport;
  routeBase: string;
  shellVariant?: string;
  seedResolver?: TSeedResolver;
};

export type TriedgeDomainRouterBaseApi = ReturnType<typeof createDomainRouterBaseApi>;

export type TriedgeFeatureBundle = {
  id: string;
  api?: TriedgeDomainRouterBaseApi;
  reducers?: ReducersMapObject;
  middleware?: Middleware[];
  hooks?: Record<string, unknown>;
  routeManifest?: unknown;
  pageRegistry?: Record<string, unknown>;
  storybookFixtures?: unknown;
};

export type CreateDomainRouterBaseApiOptions = {
  reducerPath?: string;
  tagTypes?: string[];
};

export type CreateFeatureBundleStoreOptions<
  TExtraReducers extends Record<string, Reducer> = Record<never, never>,
> = {
  bundles: TriedgeFeatureBundle[];
  extraReducers?: TExtraReducers;
  extraMiddleware?: Middleware[];
  preloadedState?: Record<string, unknown>;
  devTools?: boolean;
};

const toCacheKeyValue = (value: unknown): CacheKeyValue => {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => toCacheKeyValue(entry));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry != null && !(typeof entry === 'string' && entry.trim() === ''))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, toCacheKeyValue(entry)] as const);
    return Object.fromEntries(entries);
  }
  return String(value);
};

const buildCacheKey = (scope: string, value: unknown): string =>
  `${scope}:${JSON.stringify(toCacheKeyValue(value))}`;

export const createDomainRouterBaseApi = ({
  reducerPath = 'triedgeDomainRouterApi',
  tagTypes = [],
}: CreateDomainRouterBaseApiOptions = {}) =>
  createApi({
    reducerPath,
    baseQuery: fakeBaseQuery<BaseApiError>(),
    serializeQueryArgs: ({ endpointName, queryArgs }) => buildCacheKey(endpointName, queryArgs),
    tagTypes,
    endpoints: () => ({}),
  });

export const composeFeatureBundles = (bundles: TriedgeFeatureBundle[]) => {
  const reducers: ReducersMapObject = {};
  const middleware: Middleware[] = [];
  const apis: TriedgeDomainRouterBaseApi[] = [];

  for (const bundle of bundles) {
    if (bundle.api) {
      reducers[bundle.api.reducerPath] = bundle.api.reducer as Reducer;
      middleware.push(bundle.api.middleware);
      apis.push(bundle.api);
    }

    if (bundle.reducers) {
      Object.assign(reducers, bundle.reducers);
    }

    if (bundle.middleware) {
      middleware.push(...bundle.middleware);
    }
  }

  return {
    bundles,
    reducers,
    middleware,
    apis,
  };
};

export const createFeatureBundleStore = <
  TExtraReducers extends Record<string, Reducer> = Record<never, never>,
>({
  bundles,
  extraReducers,
  extraMiddleware = [],
  preloadedState,
  devTools = true,
}: CreateFeatureBundleStoreOptions<TExtraReducers>) => {
  const composed = composeFeatureBundles(bundles);
  const reducerMap = {
    ...(extraReducers ?? ({} as TExtraReducers)),
    ...composed.reducers,
  } as TExtraReducers & typeof composed.reducers;

  return configureStore({
    reducer: reducerMap,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(...composed.middleware, ...extraMiddleware),
    preloadedState,
    devTools,
  });
};
